import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form, Input, Button, Breadcrumb, Switch, Select, message } from "antd";
import BackButton from "../../components/reuseComp/BackButton";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import {
  useSubAdminDetail,
  useSubAdminCreate,
  useSubAdminUpdate,
} from "../../../hooks/api/subAdmins/hooks";

import { subAdminSchema } from "../../components/validations";
import { useMessageApi } from "../../../provider/AntdNotification";
import { AdminRightsTypes } from "../../../types/api/subAdmin";

export default function SubAdminForm() {
  const messageApi = useMessageApi();

  const navigate = useNavigate();
  const { id } = useParams();

  const { data: detail, isLoading } = useSubAdminDetail(id ?? "");

  const { mutate: saveSubAdmin, isPending } = useSubAdminCreate({
    onSuccess: () => {
      messageApi.success(
        "Sub Admin created successfully"
      );
      navigate("/sub-admins");
    },
    onError: () => {
      messageApi.error("Something went wrong");
    },
  });

  const { mutate: updateUser } = useSubAdminUpdate({
    onSuccess: () => {
      messageApi.success("Sub Admin updated successfully");
    },
    onError: (error: any) => messageApi.error(error.message),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(subAdminSchema) as any,

    defaultValues: {
      name: "",
      email: "",
      phone: "",
      rights: [] as AdminRightsTypes[],
      status: true as boolean,
      user_type: "SUB_ADMIN",
      password: "",
    },
  });

  // Prefill values on edit mode
  useEffect(() => {
    if (detail?.data?._id) {
      reset({
        name: detail.data.name ?? "",
        email: detail.data.email ?? "",
        phone: detail.data.phone ?? "",
        rights: detail.data.rights ?? [],
        status: detail.data.status ?? true,
        user_type: detail.data.user_type ?? "SUB_ADMIN",
        password: "",
      } as any);
    }
  }, [detail]);

  const onSubmit = (formData: any) => {
    const payload = { ...formData };
    if (!id) {
      messageApi.error("Password is Required");
    }
    if (id) {
      updateUser({...payload ,_id : id});
    } else {
      saveSubAdmin(payload);
    }
  };

  // Rights list (customize if needed)
  const RIGHTS_OPTIONS = [
    { value: "DASHBOARD", label: "Dashboard" },
    { value: "USERS", label: "Users" },
    { value: "CONSULTATION_BOOKING", label: "Consultation Booking" },
    { value: "ACCOUNTANT", label: "Accountant" },
    { value: "SETTING", label: "Setting" },
  ];

  return (
    <div>
      <div className="mb-6">
        <BackButton>Back</BackButton>

        <h1 className="text-lg sm:text-3xl font-semibold sm:mb-1">
          {id ? "Update Sub Admin" : "Create Sub Admin"}
        </h1>

        <Breadcrumb
          separator=">"
          items={[{ title: "Dashboard" }, { title: "Sub Admins" }]}
          className="text-xs sm:text-base"
        />
      </div>

      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <div className="bg-[#FEF2E7] p-4 rounded-xl grid md:grid-cols-2 gap-4">
          {/* Name */}
          <Form.Item
            label="Name"
            validateStatus={errors.name ? "error" : ""}
            help={errors.name?.message}
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="Enter name" />
              )}
            />
          </Form.Item>

          {/* Email */}
          <Form.Item
            label="Email"
            validateStatus={errors.email ? "error" : ""}
            help={errors.email?.message}
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="Enter email" />
              )}
            />
          </Form.Item>

          {/* Phone */}
          <Form.Item
            label="Phone"
            validateStatus={errors.phone ? "error" : ""}
            help={errors.phone?.message}
          >
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="Enter phone" />
              )}
            />
          </Form.Item>

          <Form.Item
            label="Password"
            validateStatus={errors.password ? "error" : ""}
            help={errors.password?.message}
          >
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <Input.Password {...field} placeholder="Enter password" />
              )}
            />
          </Form.Item>

          {/* Rights */}
          <Form.Item label="Assign Rights">
            <Controller
              name="rights"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  mode="multiple"
                  allowClear
                  className="h-[43px]"
                  placeholder="Select rights"
                  options={RIGHTS_OPTIONS}
                />
              )}
            />
          </Form.Item>

          {/* Status */}

          <Form.Item label="Status">
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  allowClear
                  placeholder="Select Status"
                  className="h-[43px]"
                  options={[
                    { value: true, label: "Active" },
                    { value: false, label: "In Active" },
                  ]}
                />
              )}
            />
          </Form.Item>
        </div>

        <div className="p-4">
          <Button
            type="primary"
            htmlType="submit"
            loading={isPending}
            className="bg-primary h-[38px] w-[150px]"
          >
            {id ? "Update" : "Save"}
          </Button>
        </div>
      </Form>
    </div>
  );
}
